(function openAiAdAttribution() {
  'use strict';

  const STORAGE_KEY = 'gid_openai_ad_attribution_v1';
  const SYNC_KEY = 'gid_openai_cart_attribution_v1';
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const CHATGPT_SOURCES = new Set(['chatgpt', 'chatgpt.com', 'openai']);
  const PAID_MEDIA = new Set(['paid', 'cpc', 'chatgpt_ads']);

  const landingUrl = new URL(window.location.href);
  const landingAttribution = attributionFromUrl(landingUrl);

  document.addEventListener('visitorConsentCollected', handleConsentChange);
  initialize();

  function initialize() {
    const shopify = window.Shopify;
    if (!shopify?.loadFeatures) return;

    shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: '0.1' }],
      (error) => {
        if (error) return;

        const customerPrivacy = window.Shopify?.customerPrivacy;
        if (customerPrivacy?.marketingAllowed?.()) persistAndSync();
      },
    );
  }

  /**
   * @param {CustomEvent} event
   */
  function handleConsentChange(event) {
    if (event.detail?.marketingAllowed) {
      persistAndSync();
      return;
    }

    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(SYNC_KEY);
    void updateCart(emptyCartAttributes());
  }

  function persistAndSync() {
    const storedAttribution = readStoredAttribution();
    const attribution = landingAttribution || storedAttribution;
    if (!attribution) return;

    if (landingAttribution) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(landingAttribution));
    }

    const attributes = cartAttributes(attribution);
    const signature = JSON.stringify(attributes);
    if (sessionStorage.getItem(SYNC_KEY) === signature) return;

    void updateCart(attributes).then((updated) => {
      if (updated) sessionStorage.setItem(SYNC_KEY, signature);
    });
  }

  /**
   * @param {URL} url
   * @returns {object|null}
   */
  function attributionFromUrl(url) {
    const source = clean(url.searchParams.get('utm_source'), 100).toLowerCase();
    const medium = clean(url.searchParams.get('utm_medium'), 100).toLowerCase();
    const oppref = clean(url.searchParams.get('oppref'), 512);
    const isPaidChatGpt = CHATGPT_SOURCES.has(source) && PAID_MEDIA.has(medium);
    if (!oppref && !isPaidChatGpt) return null;

    return {
      oppref,
      utm_source: source,
      utm_medium: medium,
      utm_campaign: clean(url.searchParams.get('utm_campaign'), 255),
      utm_content: clean(url.searchParams.get('utm_content'), 255),
      utm_term: clean(url.searchParams.get('utm_term'), 255),
      landing_path: clean(`${url.pathname}${url.hash}`, 512),
      captured_at: new Date().toISOString(),
    };
  }

  /**
   * @returns {object|null}
   */
  function readStoredAttribution() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      const capturedAt = Date.parse(stored?.captured_at);
      if (!capturedAt || Date.now() - capturedAt > MAX_AGE_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return stored;
    } catch (error) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  /**
   * @param {object} attribution
   * @returns {object}
   */
  function cartAttributes(attribution) {
    return {
      openai_oppref: attribution.oppref || '',
      openai_utm_source: attribution.utm_source || '',
      openai_utm_medium: attribution.utm_medium || '',
      openai_utm_campaign: attribution.utm_campaign || '',
      openai_utm_content: attribution.utm_content || '',
      openai_utm_term: attribution.utm_term || '',
      openai_landing_path: attribution.landing_path || '',
      openai_captured_at: attribution.captured_at || '',
    };
  }

  function emptyCartAttributes() {
    return cartAttributes({});
  }

  /**
   * @param {object} attributes
   * @returns {Promise<boolean>}
   */
  async function updateCart(attributes) {
    try {
      const root = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
      const endpoint = new URL('cart/update.js', root);
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * @param {string|null} value
   * @param {number} maximumLength
   * @returns {string}
   */
  function clean(value, maximumLength) {
    return String(value || '').trim().slice(0, maximumLength);
  }
})();
